import { forwardRef, useEffect, useImperativeHandle, useState } from 'react'

import { NumberInput, Select, Text } from '@mantine/core'
import { TimeInput } from '@mantine/dates'

import { useMutation, useQuery } from '@redwoodjs/web'

import { toast } from 'src/components/Overlays/Toast/Toast'
import { useCurrentCompanyContext } from 'src/context/CurrentCompanyContext'
import {
  GET_VOE_CALL_SCHEDULING,
  UPDATE_VOE_CALL_SCHEDULING,
} from 'src/graphql/companyVoEOutreachConfig.graphql'
import {
  formatMinutesLabel,
  hhmmToMinutes,
  minutesToHHMM,
} from 'src/pages/SettingsPage/tabs/RulesTab/components/RuleActiveHours'

import FieldLabel from '../FieldLabel'

import {
  CALL_SCHEDULING_LIMITS,
  DEFAULT_CALL_SCHEDULING,
  DEFAULT_CALL_TIMEZONE,
  formatInterval,
  timezoneAbbreviation,
  validateCallScheduling,
  type VoECallSchedulingValues,
} from './callScheduling'

export interface VoECallingSectionRef {
  save: () => Promise<void>
  cancel: () => void
  /** A user-facing error that should block saving, or null. */
  validationError: string | null
  /** Turns Automated Calling on, e.g. once the user confirms a prompt. */
  enable: () => void
}

interface VoECallingSectionProps {
  companyId: string
  editing: boolean
  /** Reports Automated Calling as shown, saved or mid-edit. */
  onEnabledChange?: (enabled: boolean) => void
  /**
   * Called when the user picks Enabled. Return false to hold the change, e.g.
   * to ask first; call enable() on the ref to apply it.
   */
  confirmEnable?: () => boolean
}

/**
 * "VoE Voice Agent" — the automated VoE calling settings (ENG-4483).
 * Lives inside the Automated Outreach card and follows its Edit/Save flow:
 * the parent calls save() after saving the email outreach settings.
 */
const VoECallingSection = forwardRef<
  VoECallingSectionRef,
  VoECallingSectionProps
>(({ companyId, editing, onEnabledChange, confirmEnable }, ref) => {
  const currentCompany = useCurrentCompanyContext()
  const timezone = currentCompany?.timezone

  const { data } = useQuery(GET_VOE_CALL_SCHEDULING, {
    variables: { companyId },
    skip: !companyId,
  })

  const saved: VoECallSchedulingValues = data?.companyVoEOutreachConfig
    ? {
        callSchedulingEnabled:
          data.companyVoEOutreachConfig.callSchedulingEnabled,
        callWindowStartMinute:
          data.companyVoEOutreachConfig.callWindowStartMinute,
        callWindowEndMinute: data.companyVoEOutreachConfig.callWindowEndMinute,
        callRetryIntervalMinutes:
          data.companyVoEOutreachConfig.callRetryIntervalMinutes,
        callMaxAttempts: data.companyVoEOutreachConfig.callMaxAttempts,
      }
    : DEFAULT_CALL_SCHEDULING

  const [draft, setDraft] = useState<VoECallSchedulingValues>(saved)

  // Sync from the server whenever we're not mid-edit.
  useEffect(() => {
    if (!editing) setDraft(saved)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, editing])

  useEffect(() => {
    onEnabledChange?.(draft.callSchedulingEnabled)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft.callSchedulingEnabled])

  const [updateCallScheduling] = useMutation(UPDATE_VOE_CALL_SCHEDULING, {
    refetchQueries: [
      { query: GET_VOE_CALL_SCHEDULING, variables: { companyId } },
    ],
    awaitRefetchQueries: true,
    onError: (error) =>
      toast(`Failed to update VoE calling: ${error.message}`, 'error'),
  })

  const validationError = validateCallScheduling(draft)
  const isDirty = JSON.stringify(draft) !== JSON.stringify(saved)

  useImperativeHandle(ref, () => ({
    save: async () => {
      if (!isDirty || validationError) return
      await updateCallScheduling({ variables: { companyId, input: draft } })
    },
    cancel: () => setDraft(saved),
    validationError,
    enable: () => update({ callSchedulingEnabled: true }),
  }))

  const update = (patch: Partial<VoECallSchedulingValues>) =>
    setDraft((current) => ({ ...current, ...patch }))

  const retryHours = Math.floor(draft.callRetryIntervalMinutes / 60)
  const retryMinutes = draft.callRetryIntervalMinutes % 60
  const windowInvalid = draft.callWindowEndMinute <= draft.callWindowStartMinute
  const tzLabel = `${timezone || DEFAULT_CALL_TIMEZONE} (${timezoneAbbreviation(
    timezone
  )})`

  return (
    <div className="flex flex-col gap-3">
      <div>
        <div className="mb-1 text-sm font-semibold text-doubleNickel-gray-900">
          VoE Voice Agent
        </div>
        <div className="text-xs text-doubleNickel-gray-500">
          When an outgoing verification has no email and no verification
          platform, the VoE voice agent calls the employer&apos;s phone number.
          Calling stops as soon as an email is captured or added, and the
          outreach schedule above takes over. If nothing is captured after the
          last attempt, the assigned recruiters are notified.
        </div>
      </div>

      <div className="grid grid-cols-12 gap-3">
        <div className="col-span-3">
          <FieldLabel
            label="Automated Calling"
            tooltip="Off by default. Recruiters can still call with the voice agent manually from a verification either way."
          />
          {editing ? (
            <Select
              data={[
                { value: 'enabled', label: 'Enabled' },
                { value: 'disabled', label: 'Disabled' },
              ]}
              value={draft.callSchedulingEnabled ? 'enabled' : 'disabled'}
              onChange={(val) => {
                const on = val === 'enabled'
                if (on && confirmEnable && !confirmEnable()) return
                update({ callSchedulingEnabled: on })
              }}
              classNames={{ input: 'text-sm' }}
            />
          ) : (
            <span className="text-sm text-doubleNickel-gray-700">
              {draft.callSchedulingEnabled ? 'Enabled' : 'Disabled'}
            </span>
          )}
        </div>

        <div className="col-span-3">
          <FieldLabel
            label="Dialing Window"
            tooltip={`Monday–Friday, in the employer's local time: from the employer's state, or the phone area code. If neither is known, your company timezone is used: ${tzLabel}. Applies to recruiter calls with the agent too. Calls that would land outside the window move to the next window start.`}
          />
          {editing ? (
            <div className="flex items-center gap-2">
              <TimeInput
                aria-label="Dialing window start"
                value={minutesToHHMM(draft.callWindowStartMinute)}
                onChange={(e) =>
                  update({
                    callWindowStartMinute: hhmmToMinutes(e.currentTarget.value),
                  })
                }
                classNames={{ input: 'text-sm' }}
              />
              <span className="text-doubleNickel-gray-400">–</span>
              <TimeInput
                aria-label="Dialing window end"
                value={minutesToHHMM(draft.callWindowEndMinute)}
                error={windowInvalid}
                onChange={(e) =>
                  update({
                    callWindowEndMinute: hhmmToMinutes(e.currentTarget.value),
                  })
                }
                classNames={{ input: 'text-sm' }}
              />
            </div>
          ) : (
            <span className="text-sm text-doubleNickel-gray-700">
              Mon–Fri {formatMinutesLabel(draft.callWindowStartMinute)}–
              {formatMinutesLabel(draft.callWindowEndMinute)} employer&apos;s
              local time
            </span>
          )}
        </div>

        <div className="col-span-3">
          <FieldLabel
            label="Retry Interval"
            tooltip="How long to wait before calling an unanswered number again. A voicemail counts as an attempt."
          />
          {editing ? (
            <div className="flex items-center gap-2">
              <NumberInput
                aria-label="Retry interval hours"
                min={0}
                max={168}
                allowDecimal={false}
                suffix=" h"
                value={retryHours}
                onChange={(v) =>
                  update({
                    callRetryIntervalMinutes:
                      (Number(v) || 0) * 60 + retryMinutes,
                  })
                }
                classNames={{ input: 'text-sm' }}
              />
              <NumberInput
                aria-label="Retry interval minutes"
                min={0}
                max={59}
                step={15}
                allowDecimal={false}
                suffix=" m"
                value={retryMinutes}
                onChange={(v) =>
                  update({
                    callRetryIntervalMinutes: retryHours * 60 + (Number(v) || 0),
                  })
                }
                classNames={{ input: 'text-sm' }}
              />
            </div>
          ) : (
            <span className="text-sm text-doubleNickel-gray-700">
              {formatInterval(draft.callRetryIntervalMinutes)}
            </span>
          )}
        </div>

        <div className="col-span-3">
          <FieldLabel
            label="Max Attempts"
            tooltip="Calling stops after this many calls if no email or answer was captured, and the assigned recruiters are notified."
          />
          {editing ? (
            <NumberInput
              aria-label="Max attempts"
              min={CALL_SCHEDULING_LIMITS.minMaxAttempts}
              max={CALL_SCHEDULING_LIMITS.maxMaxAttempts}
              allowDecimal={false}
              value={draft.callMaxAttempts}
              onChange={(v) => update({ callMaxAttempts: Number(v) || 0 })}
              classNames={{ input: 'text-sm' }}
            />
          ) : (
            <span className="text-sm text-doubleNickel-gray-700">
              {draft.callMaxAttempts}
            </span>
          )}
        </div>
      </div>

      {editing && validationError && (
        <Text size="xs" c="red">
          {validationError}
        </Text>
      )}
    </div>
  )
})

export default VoECallingSection
